import { LocalizedResourceDictionaryKeys } from '../../../application/localisation/i18next';
import { IDoctorStudyDto, IssuesStateEnum } from '../../../services/api/api-client';
import { ReactComponent as Opened } from '../../../assets/img/issues/opened_16.svg';
import { ReactComponent as Closed } from '../../../assets/img/issues/closed_16.svg';
import { ReactComponent as Processed } from '../../../assets/img/issues/pre_close_16.svg';
import { FunctionComponent, SVGProps } from 'react';

export type IssueQueryParamsType = {
  state?: IssuesStateEnum;
  subjectList?: string[];
  sortBy?: string;
  desc?: boolean;
  patientUid?: string;
  patientGroupList?: number[];
  unreadOnly?: boolean;
};

export type GroupedIssueQueryParamsType = {
  state?: IssuesStateEnum;
  subjectList?: string[];
  sortBy?: string;
  desc?: boolean;
  patientUid?: string;
  patientGroupList?: number[];
  unreadOnly?: boolean;
};

export type PageParamsType = {
  page: number;
  perPage: number;
};

export const defaultIssueQueryParams: IssueQueryParamsType = {
  sortBy: 'createdAt',
  desc: true,
  state: undefined,
  subjectList: undefined,
  patientUid: undefined,
  patientGroupList: undefined,
  unreadOnly: false,
};

export const defaultGroupedIssueQueryParams: GroupedIssueQueryParamsType = {
  sortBy: 'uniqueId',
  desc: true,
  state: undefined,
  subjectList: undefined,
  patientUid: undefined,
  patientGroupList: undefined,
  unreadOnly: false,
};

export const defaultPageParams: PageParamsType = {
  page: 1,
  perPage: 20,
};

export type IssueFilterProps = {
  queryParams: IssueQueryParamsType;
  onChange: (params: IssueQueryParamsType) => void;
};

type IIssueFilterOption = {
  key: 'All' | 'Opened' | 'Processed' | 'Closed';
  icon?: FunctionComponent<SVGProps<any>>;
  localizationKey: LocalizedResourceDictionaryKeys;
};

type IIssueSortingOption = {
  key: 'createdAt' | 'updatedAt' | 'deadlineAt';
  icon?: FunctionComponent<SVGProps<any>>;
  localizationKey: LocalizedResourceDictionaryKeys;
  desc: boolean;
};

export const issueStates = ['Opened', 'Processed', 'Closed'] as const;
export type IssueStatesType = (typeof issueStates)[number];

export const issueSubjects = ['Patient', 'Study'] as const;
export type IssueSubjectsType = (typeof issueSubjects)[number];

export const issueTopicsForPatient = ['GeneralInfo', 'Records', 'Analyzes', 'Surveys', 'Notes', 'Other'] as const;
export type IssueTopicsForPatientType = (typeof issueTopicsForPatient)[number];
export const issueTopicsForStudy = ['SourceDataControl', 'Documentation', 'Other'] as const;
export type IssueTopicsForStudyType = (typeof issueTopicsForStudy)[number];

export const statusOptions: IIssueFilterOption[] = [
  { key: 'All', localizationKey: 'Issues.Filters.AllStatuses' as LocalizedResourceDictionaryKeys },
  {
    key: 'Opened',
    icon: Opened,
    localizationKey: 'Issues.Status.Opened' as LocalizedResourceDictionaryKeys,
  },
  {
    key: 'Processed',
    icon: Processed,
    localizationKey: 'Issues.Status.Processed' as LocalizedResourceDictionaryKeys,
  },
  {
    key: 'Closed',
    icon: Closed,
    localizationKey: 'Issues.Status.Closed' as LocalizedResourceDictionaryKeys,
  },
];

export const sortingOptions: IIssueSortingOption[] = [
  {
    key: 'createdAt',
    desc: true,
    localizationKey: 'Issues.Sorting.ByNewest' as LocalizedResourceDictionaryKeys,
  },
  {
    key: 'updatedAt',
    desc: true,
    localizationKey: 'Issues.Sorting.ByUpdate' as LocalizedResourceDictionaryKeys,
  },
  {
    key: 'deadlineAt',
    desc: false,
    localizationKey: 'Issues.Sorting.ByDeadline' as LocalizedResourceDictionaryKeys,
  },
];

export function filterDynamicIssueTopicsFunc(
  topic: IssueTopicsForPatientType | IssueTopicsForStudyType,
  studyConfiguration?: IDoctorStudyDto,
): boolean {
  if (topic === 'Analyzes' && studyConfiguration && !studyConfiguration.hasDbImport) {
    return false;
  }

  if (
    topic === 'Surveys' &&
    studyConfiguration &&
    !studyConfiguration.groups.some((group) => group.mobileConfiguration.isEnabled)
  ) {
    return false;
  }

  return true;
}
